import {useEffect, useState} from 'react';
import {APIClient} from './APIClient';
import {API_URL} from '../settings/local_settings';
import {LocalStorage} from './LocalStorage';
import {isOk} from './apiResponse';

/**
 * El cupo de cuentas publicitarias vinculadas del plan, leído en UN solo lugar (UD-15).
 *
 * Source of truth: `GET agency/subscription/ad-account-quota/` → `{limit, used, available}`, que el
 * backend saca de `SubscriptionService.ad_account_quota`, la MISMA cuenta que hace cumplir el tope.
 * El rechazo del alta (`CANT_ADD_MORE_AD_ACCOUNTS`) trae los mismos números en `errors`.
 *
 * Reglas que lo hacen seguro de usar en cualquier pantalla:
 * - **Nunca bloquea con un dato viejo.** Cada pantalla que monta el hook pide el cupo FRESCO (sin el
 *   cache de 30 s de `APIClient`); los pedidos simultáneos comparten el mismo request. Un cupo
 *   «lleno» que ya no lo está dejaría a la agencia sin poder vincular, que es peor que el error.
 * - **Desconocido no es lleno.** Mientras carga o si la primera lectura falla, el hook da `null` y la
 *   pantalla se comporta como antes; el backend sigue rechazando igual. Un refresco que falla no
 *   borra lo que la pantalla ya sabía.
 * - **Un refresco le llega a todas las pantallas montadas** (`refetchAdAccountQuota` después de
 *   vincular o desvincular; `publishAdAccountQuota` con los números de un rechazo).
 * - **Por sesión**: una respuesta pedida con otro token (logout + login en la misma pestaña) se
 *   descarta, igual que en `usePortalSubscriptionGating`.
 */

// El código tipado con que el alta rechaza por el tope (`SubscriptionService.ERROR_CODE_CANT_ADD_MORE_AD_ACCOUNTS`).
export const PLAN_LIMIT_CODE = 'CANT_ADD_MORE_AD_ACCOUNTS';

const QUOTA_URL = 'agency/subscription/ad-account-quota/';

/** `{limit, used, available}` si los tres son números; si no, `null`. */
export function quotaOf(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const {limit, used, available} = raw;
    if (![limit, used, available].every((value) => typeof value === 'number' && Number.isFinite(value))) return null;
    return {limit, used, available};
}

/** ¿El cupo está lleno? `false` si no se sabe: desconocido no es lleno. */
export function quotaIsFull(quota) {
    return !!quota && quota.available <= 0;
}

/** ¿La respuesta es el rechazo por el tope del plan? */
export function isPlanLimitError(response) {
    return !!response && (response.error_code || response.status) === PLAN_LIMIT_CODE;
}

/** El cupo que viaja en el rechazo del alta (`errors`, texto JSON), o `null`. */
export function quotaFromLinkError(response) {
    if (!isPlanLimitError(response)) return null;
    const errors = response.errors;
    if (!errors) return null;
    try {
        return quotaOf(typeof errors === 'string' ? JSON.parse(errors) : errors);
    } catch (e) {
        return null;
    }
}

let inFlight = null;
let inFlightToken = null;
// Sólo el pedido más nuevo difunde: uno viejo que resuelve tarde no pisa un valor fresco.
let requestSeq = 0;
const listeners = new Set();

function currentToken() {
    return LocalStorage.fetchUserToken() || null;
}

function broadcast(quota) {
    listeners.forEach((listener) => listener(quota));
}

function request() {
    const seq = ++requestSeq;
    const token = currentToken();
    // Dentro del `then`: hasta un error sincrónico al armar el pedido termina en «desconocido».
    const promise = Promise.resolve()
        .then(() => new APIClient(API_URL + QUOTA_URL).call('GET'))
        .catch(() => null)
        .then((response) => {
            const quota = isOk(response) ? quotaOf(response.data) : null;
            const stale = seq !== requestSeq || token !== currentToken();
            // Un pedido que falla no difunde nada: cada pantalla conserva lo que ya sabía (una que recién
            // monta sigue en `null`). Si no, un refresco caído borraba el «lleno» que acababa de traer un
            // rechazo y el cartel desaparecía (como en `usePortalSubscriptionGating`).
            if (!stale && quota) broadcast(quota);
            return quota;
        })
        .finally(() => {
            if (inFlight === promise) {
                inFlight = null;
                inFlightToken = null;
            }
        });
    inFlight = promise;
    inFlightToken = token;
    return promise;
}

/** Pide el cupo fresco, compartiendo el pedido en vuelo de esta sesión. Resuelve con el cupo o `null`. */
export function fetchAdAccountQuota() {
    if (inFlight && inFlightToken === currentToken()) return inFlight;
    return request();
}

/** Vuelve a pedirlo aunque haya uno en vuelo (después de vincular o desvincular). */
export function refetchAdAccountQuota() {
    return request();
}

/** Difunde un cupo que llegó por otro lado (el rechazo del alta) sin pedir nada. */
export function publishAdAccountQuota(raw) {
    const quota = quotaOf(raw);
    if (!quota) return;
    // Lo que esté en vuelo ya es más viejo que este dato: no difunde, y una pantalla que monte después
    // no lo reusa (se quedaría en `null` esperando un pedido que se va a descartar).
    requestSeq += 1;
    inFlight = null;
    inFlightToken = null;
    broadcast(quota);
}

/**
 * El cupo para una pantalla: `null` mientras carga, si falla o con `enabled` en `false`. Se pide de
 * nuevo cada vez que `enabled` pasa a `true` (un modal que se abre) y cuando cambia la sesión.
 */
export function useAdAccountQuota({enabled = true} = {}) {
    const [quota, setQuota] = useState(null);
    const token = currentToken();

    useEffect(() => {
        if (!enabled) {
            setQuota(null);
            return undefined;
        }
        let alive = true;
        const listener = (next) => { if (alive) setQuota(next); };
        listeners.add(listener);
        setQuota(null);
        fetchAdAccountQuota();
        return () => {
            alive = false;
            listeners.delete(listener);
        };
    }, [enabled, token]);

    return quota;
}

/** Sólo para tests: el estado es de módulo y sobrevive entre casos. */
export function __resetAdAccountQuota() {
    inFlight = null;
    inFlightToken = null;
    requestSeq = 0;
    listeners.clear();
}
